import React from "react";
import PropTypes from 'prop-types';
import { Table, Container, Row, Col, Button, Badge } from "react-bootstrap";

const norm = v => v || '';
// Values may be numbers (e.g. idAxonaut) — normalize to string before splitting
const normMulti = s => {
    const v = s == null ? '' : String(s);
    return (!v || v === '-') ? v : v.split(', ').sort().join(', ');
};

const formatAddress = (num, route, cp, locality, country) => {
    const street = [num, route].filter(Boolean).join(' ') || null;
    return [street, cp, locality, country].filter(Boolean).join(', ') || '-';
};

const resolveNames = (ids, list, idKey = 'id', nameKey = 'name') =>
    (ids ?? []).map(id => list?.find(item => String(item[idKey]) === String(id))?.[nameKey] ?? String(id)).join(', ') || '-';

const resolveStatus = (val, list) =>
    list?.find(s => String(s.id) === String(val))?.name ?? (val ? String(val) : '-');

const CONTACT_FIELDS = { name: 'Nom', jobTitle: 'Fonction', email: 'Email', phone: 'Téléphone' };

const TYPE_META = {
    changed: { bg: '#fff3cd', badge: 'warning', label: 'Modifié' },
};

const TypeBadge = ({ type }) => {
    const meta = TYPE_META[type];
    if (!meta) return null;
    return <Badge bg={meta.badge} className="m-2 p-2">{meta.label}</Badge>;
};

TypeBadge.propTypes = { type: PropTypes.string };

const CLIENT_FIELDS = [
    { key: 'clientName',     label: 'Nom client' },
    { key: 'siret',          label: 'SIRET' },
    { key: 'uai',            label: 'UAI' },
    { key: 'idAxonaut',      label: 'ID Axonaut' },
    { key: 'backofficeUUID', label: 'Client BO (UUID)' },
    { key: 'address',        label: 'Adresse' },
    { key: 'parentClient',   label: 'Client Parent' },
    { key: 'categories',     label: 'Catégories' },
    { key: 'tagOperations',  label: 'Tag Operations' },
    { key: 'status',         label: 'Statut' },
    { key: 'natureRelation', label: 'Nature de la relation' },
    { key: 'contactsM360', label: 'Chargé·es de clientèle' },
];

const ContactDisplay = ({ contact }) => <>
    <div>{contact.name || '-'}</div>
    {contact.jobTitle && <div className="text-muted small">{contact.jobTitle}</div>}
    {contact.email && <div className="text-muted small">{contact.email}</div>}
    {contact.phone && <div className="text-muted small">{contact.phone}</div>}
</>;

ContactDisplay.propTypes = { contact: PropTypes.object.isRequired };

const PipedriveValidationPage = ({ pipedriveData, boData, note, lookups, message, onBack, onConfirm, isSubmitting }) => {
    const hasBo = boData != null;

    const formData = message?.newData;

    if (!formData) return null;

    const parentUuid = formData.parentClientUuid?.[0];
    const savedValues = {
        clientName: formData.clientName,
        siret: formData.siret,
        uai: formData.uai,
        idAxonaut: formData.idAxonaut,
        backofficeUUID: Array.isArray(formData.backofficeUUID) ? (formData.backofficeUUID[0] ?? null) : formData.backofficeUUID,
        address: formData.addressLocality == null ? null : formatAddress(formData.addressStreetNumber, formData.addressRoute, formData.addressPostalCode, formData.addressLocality, formData.addressCountry),
        parentClient: formData.parentClientUuid == null ? null
            : formData.parentClientUuid?.length === 0 ? '-'
                : parentUuid ? (lookups.allClients?.find(c => c.uniqueId === parentUuid)?.clientName ?? parentUuid) : '-',
        categories: resolveNames(formData.categories, lookups.clientsCategories),
        tagOperations: resolveNames(formData.tagOperations, lookups.allTagOperations),
        status: resolveStatus(formData.clientStatus, lookups.allClientStatus),
        natureRelation: resolveNames(formData.natureRelation, lookups.allRelationTypes),
        contactsM360: resolveNames(formData.contactsM360, lookups.contactsM360List),
    };

    const clientRows = CLIENT_FIELDS.map(({ key, label }) => {
        const sv = savedValues[key];
        const pipVal = pipedriveData[key] ?? '-';
        // parentClient may be a JSX node (related-relation warning) — compare on its text variant
        const pipCompare = key === 'parentClient' ? (pipedriveData.parentClientText ?? '-') : pipVal;
        const boVal = hasBo ? (boData[key] ?? '-') : null;
        const type = sv == null ? 'unchanged' : normMulti(sv ?? '-') === normMulti(pipCompare) ? 'unchanged' : 'changed';
        return {
            key, label,
            pip: pipVal,
            bo: boVal,
            saved: sv ?? '-',
            type,
            boChanged: hasBo && sv != null && normMulti(String(sv ?? '-')) !== normMulti(String(boVal ?? '-')),
        };
    });

    const pipContacts = pipedriveData.contacts ?? [];
    const boContacts = boData?.contacts ?? [];

    const contactRows = pipContacts.map(pipContact => {
        const boContact = hasBo ? boContacts.find(c => c.idPipedrive == pipContact.id || c.email === pipContact.email) : null;
        const savedContact = formData.contacts?.find(c => c.id === pipContact.id);
        if (!savedContact) return null;
        const changed = Object.keys(CONTACT_FIELDS).some(f => norm(pipContact[f]) !== norm(savedContact[f]));
        return { key: `contact-${pipContact.id}`, type: changed ? 'changed' : 'unchanged', pipContact, savedContact, boContact };
    }).filter(Boolean);

    return (
        <Container fluid>
            <Row className="mb-3">
                <Col><h4 className="mb-0 text-muted">Récapitulatif des modifications</h4></Col>
            </Row>
            <h3 className="mb-4 mt-4">Client</h3>
            <Table bordered>
                <thead>
                    <tr>
                        <th style={{ width: hasBo ? '15%' : '20%' }}>Champ</th>
                        <th style={{ width: hasBo ? '26%' : '37%' }}>Valeurs entrantes</th>
                        {hasBo && <th style={{ width: '26%' }}>BO actuel</th>}
                        <th style={{ width: hasBo ? '27%' : '37%' }}>Valeurs enregistrées</th>
                        <th style={{ width: '6%' }} />
                    </tr>
                </thead>
                <tbody>
                    {clientRows.map(row => (
                        <tr key={row.key} style={{ backgroundColor: TYPE_META[row.type]?.bg ?? 'transparent' }}>
                            <td className="fw-semibold">{row.label}</td>
                            <td>{row.pip}</td>
                            {hasBo && <td>{row.bo}</td>}
                            <td>{row.saved}</td>
                            <td><TypeBadge type={row.type} /></td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            {contactRows.length > 0 && <>
                <h3 className="mb-4 mt-4">Contacts</h3>
                <Table bordered>
                    <thead>
                        <tr>
                            <th style={{ width: hasBo ? '20%' : '25%' }}>Contact</th>
                            <th style={{ width: hasBo ? '28%' : '37%' }}>Valeurs entrantes</th>
                            {hasBo && <th style={{ width: '28%' }}>BO actuel</th>}
                            <th style={{ width: hasBo ? '18%' : '32%' }}>Valeurs enregistrées</th>
                            <th style={{ width: '6%' }} />
                        </tr>
                    </thead>
                    <tbody>
                        {contactRows.map(row => {
                            const bg = TYPE_META[row.type]?.bg ?? 'transparent';
                            const contactName = row.pipContact?.name || row.boContact?.name || '-';
                            return (
                                <tr key={row.key} style={{ backgroundColor: bg }}>
                                    <td className="fw-semibold">{contactName}</td>
                                    <td>{row.pipContact ? <ContactDisplay contact={row.pipContact} /> : <span className="text-muted fst-italic">—</span>}</td>
                                    {hasBo && <td>{row.boContact ? <ContactDisplay contact={row.boContact} /> : <span className="text-muted fst-italic">Non trouvé dans le BO</span>}</td>}
                                    <td><ContactDisplay contact={row.savedContact} /></td>
                                    <td><TypeBadge type={row.type} /></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </>}

            {note && (
                <Row className="mt-3">
                    <Col>
                        <span className="fw-semibold me-2">Note :</span>
                        <span className="text-muted">{note}</span>
                    </Col>
                </Row>
            )}

            <h3 className="mb-4 mt-4">Actions qui en découlent:</h3>
            {(() => {
                const pipClientChanged = clientRows.some(r => r.type === 'changed');
                const boClientChanged = clientRows.some(r => r.boChanged);
                const pipContactsChanged = contactRows.some(r => r.type === 'changed');
                const boContactsChanged = hasBo && contactRows.some(r =>
                    r.boContact && r.savedContact &&
                    Object.keys(CONTACT_FIELDS).some(f => norm(r.savedContact[f]) !== norm(r.boContact[f]))
                );
                const newBoContacts = hasBo && contactRows.some(r => !r.boContact);
                const axonautModify = message?.type === 'deal' && !!formData.idAxonaut;
                const axonautCreate = message?.type === 'deal' && !formData.idAxonaut;
                const actions = [
                    !hasBo                && 'Création du client BO',
                    boClientChanged       && 'Modifications du client BO',
                    pipClientChanged      && 'Modification des informations clients Pipedrive',
                    pipContactsChanged    && "Modification d'informations de contact Pipedrive",
                    boContactsChanged     && "Modification d'informations de contact BO",
                    newBoContacts         && 'Création de contacts dans le BO issus de Pipedrive',
                    axonautModify         && 'Modification du client Axonaut',
                    axonautCreate         && "Création d'un client Axonaut",
                    message?.type === 'deal' && "Création d'un devis sur Axonaut"
                ].filter(Boolean);
                return actions.length > 0
                    ? <ul>{actions.map(a => <li key={a}>{a}</li>)}</ul>
                    : <p className="text-muted fst-italic">Aucune action détectée.</p>;
            })()}

            <Row className="mt-3">
                <Col className="d-flex gap-2 justify-content-end">
                    <Button variant="secondary" disabled={isSubmitting} onClick={onBack}>Modifier</Button>
                    <Button variant="success" disabled={isSubmitting} onClick={onConfirm}>Confirmer</Button>
                </Col>
            </Row>
        </Container>
    );
};

PipedriveValidationPage.propTypes = {
    pipedriveData: PropTypes.object.isRequired,
    boData: PropTypes.object,
    note: PropTypes.string,
    lookups: PropTypes.object.isRequired,
    message: PropTypes.object.isRequired,
    onBack: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool,
};

export default PipedriveValidationPage;
